import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MoreVertical } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { spacing } from '../../config/spacing';
import { typography } from '../../config/typography';
import { Card } from '../UI/card';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Checkbox } from '../UI/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../UI/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../UI/pagination';
import { getRegionFromCountry, getRiskTier } from '../../utils/companyDisplayUtils';

const PAGE_SIZE_DEFAULT = 5;
const RISK_TIERS = ['high', 'medium', 'low'];
const REGIONS = ['Asia Pacific', 'Europe', 'Americas', 'Middle East', 'Africa'];

function formatRelativeTime(dateStr, t) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return t('timeMinsAgo', { count: diffMins });
  if (diffHours < 24) return t('timeHoursAgo', { count: diffHours });
  if (diffDays === 1) return t('timeYesterday');
  if (diffDays < 7) return t('timeDaysAgo', { count: diffDays });
  return date.toLocaleDateString();
}

function getStatus(item) {
  if (item.priority === 'high') return 'statusHighPriority';
  if ((item.userCompanyInfo?.routes?.length || 0) > 0) return 'statusActive';
  return 'statusInactive';
}

function getRiskLevel(item) {
  if (item.priority === 'high') return 'riskLevelHigh';
  if (item.priority === 'low') return 'riskLevelLow';
  return 'riskLevelModerate';
}

function getRiskLevelWidth(item) {
  if (item.priority === 'high') return 90;
  if (item.priority === 'low') return 25;
  return 55;
}

/**
 * Reusable watchlist activity table: search, filters (industry, region, risk tier, compliance),
 * paginated table (Company, Status, Risk Level, Last Updated, Routes, Actions).
 * Uses design system (colors, typography, spacing). Same layout as Dashboard recent watchlist table.
 */
function WatchlistTable({
  watchlist = [],
  companies = [],
  title,
  subtitle,
  emptyMessage,
  pageSize = PAGE_SIZE_DEFAULT,
  cardStyle: cardStyleProp,
  showPriorityFilter = false,
}) {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterRiskTier, setFilterRiskTier] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [complianceFlags, setComplianceFlags] = useState(false);

  const cardStyle = useMemo(
    () => ({
      backgroundColor: colors.background.card ?? colors.background.secondary,
      borderColor: colors.border.light,
      boxShadow: spacing.shadow?.lg ?? '0 6px 18px rgba(32,41,50,0.04)',
      borderRadius: spacing.radius?.lg ?? 12,
      ...cardStyleProp,
    }),
    [cardStyleProp]
  );
  const cardTitleStyle = typography.textStyles?.cardTitle || typography.textStyles?.h2 || {};

  const industries = useMemo(() => {
    const ids = new Set(watchlist.map((i) => i.companyId ?? i.company_id).filter(Boolean));
    return [
      ...new Set(
        companies
          .filter((c) => ids.has(c.ticker) || ids.has(c.id))
          .map((c) => c.industry)
          .filter(Boolean)
      ),
    ].sort();
  }, [watchlist, companies]);

  const companyById = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      map[c.id] = c;
      if (c.ticker) map[c.ticker] = c;
    });
    return map;
  }, [companies]);

  const filteredWatchlist = useMemo(() => {
    let filtered = watchlist;
    const q = search.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((item) => {
        const company = companyById[item.companyId];
        const name = (company?.name || item.companyId || '').toLowerCase();
        return name.includes(q) || String(item.companyId).toLowerCase().includes(q);
      });
    }
    if (showPriorityFilter && filterPriority !== 'all') {
      filtered = filtered.filter((item) => (item.priority || 'normal') === filterPriority);
    }
    if (filterIndustry !== 'all') {
      filtered = filtered.filter((item) => {
        const company = companyById[item.companyId];
        return company?.industry === filterIndustry;
      });
    }
    if (filterRegion !== 'all') {
      filtered = filtered.filter((item) => {
        const company = companyById[item.companyId];
        return getRegionFromCountry(company?.country) === filterRegion;
      });
    }
    if (filterRiskTier !== 'all') {
      filtered = filtered.filter((item) => {
        const company = companyById[item.companyId];
        return getRiskTier(company?.overallRiskScore) === filterRiskTier;
      });
    }
    if (complianceFlags) {
      filtered = filtered.filter((item) => {
        const company = companyById[item.companyId];
        return company?.riskCategories && company.riskCategories.length > 0;
      });
    }
    return filtered;
  }, [
    watchlist,
    companyById,
    search,
    showPriorityFilter,
    filterPriority,
    filterIndustry,
    filterRegion,
    filterRiskTier,
    complianceFlags,
  ]);

  const paginatedWatchlist = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredWatchlist.slice(start, start + pageSize);
  }, [filteredWatchlist, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredWatchlist.length / pageSize));
  const from = filteredWatchlist.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, filteredWatchlist.length);

  const clearFilters = () => {
    setFilterIndustry('all');
    setFilterRegion('all');
    setFilterRiskTier('all');
    setFilterPriority('all');
    setComplianceFlags(false);
    setPage(1);
  };

  const hasActiveFilters =
    filterIndustry !== 'all' ||
    filterRegion !== 'all' ||
    filterRiskTier !== 'all' ||
    (showPriorityFilter && filterPriority !== 'all') ||
    complianceFlags;

  const handleView = (companyId) => {
    navigate(`/companies/${companyId}`);
  };

  return (
    <Card style={cardStyle} className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h3
            className="font-semibold"
            style={{ color: colors.text.primary, ...cardTitleStyle }}
          >
            {title}
          </h3>
          {subtitle != null && subtitle !== '' && (
            <p className="text-sm mt-1" style={{ color: colors.text.secondary }}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-row items-center gap-2 flex-nowrap min-h-10">
          <Input
            placeholder={t('searchWatchlistPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="max-w-xs w-full min-w-[180px] h-10"
            style={{ backgroundColor: colors.header?.searchBg }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="default"
                className="border flex items-center gap-1 h-10 flex-shrink-0"
                style={{
                  borderColor: colors.border.light,
                  color: colors.text.secondary,
                  ...(hasActiveFilters
                    ? { borderColor: colors.primary[500], color: colors.primary[600] }
                    : {}),
                }}
              >
                {t('filter')} <ChevronDown style={{ width: 14, height: 14 }} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[240px] p-4"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                <p className="text-sm font-medium" style={{ color: colors.text.primary }}>
                  {t('filtersLabel')}
                </p>
                {showPriorityFilter && (
                  <div>
                    <label className="text-xs block mb-1" style={{ color: colors.text.secondary }}>
                      {t('priority')}
                    </label>
                    <select
                      value={filterPriority}
                      onChange={(e) => {
                        setFilterPriority(e.target.value);
                        setPage(1);
                      }}
                      className="w-full rounded border text-sm px-2 py-1.5"
                      style={{
                        borderColor: colors.border.light,
                        color: colors.text.primary,
                        backgroundColor: colors.background.secondary,
                      }}
                    >
                      <option value="all">{t('allPriorities')}</option>
                      <option value="high">{t('riskLevelHigh')}</option>
                      <option value="medium">{t('riskLevelModerate')}</option>
                      <option value="low">{t('riskLevelLow')}</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs block mb-1" style={{ color: colors.text.secondary }}>
                    {t('industryLabel')}
                  </label>
                  <select
                    value={filterIndustry}
                    onChange={(e) => {
                      setFilterIndustry(e.target.value);
                      setPage(1);
                    }}
                    className="w-full rounded border text-sm px-2 py-1.5"
                    style={{
                      borderColor: colors.border.light,
                      color: colors.text.primary,
                      backgroundColor: colors.background.secondary,
                    }}
                  >
                    <option value="all">{t('allIndustries')}</option>
                    {industries.map((ind) => (
                      <option key={ind} value={ind}>
                        {ind}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs block mb-1" style={{ color: colors.text.secondary }}>
                    {t('regionLabel')}
                  </label>
                  <select
                    value={filterRegion}
                    onChange={(e) => {
                      setFilterRegion(e.target.value);
                      setPage(1);
                    }}
                    className="w-full rounded border text-sm px-2 py-1.5"
                    style={{
                      borderColor: colors.border.light,
                      color: colors.text.primary,
                      backgroundColor: colors.background.secondary,
                    }}
                  >
                    <option value="all">{t('allRegions')}</option>
                    {REGIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs block mb-1" style={{ color: colors.text.secondary }}>
                    {t('riskTierLabel')}
                  </label>
                  <select
                    value={filterRiskTier}
                    onChange={(e) => {
                      setFilterRiskTier(e.target.value);
                      setPage(1);
                    }}
                    className="w-full rounded border text-sm px-2 py-1.5"
                    style={{
                      borderColor: colors.border.light,
                      color: colors.text.primary,
                      backgroundColor: colors.background.secondary,
                    }}
                  >
                    <option value="all">{t('allRiskTiers')}</option>
                    {RISK_TIERS.map((tier) => (
                      <option key={tier} value={tier}>
                        {t(
                          `riskLevel${tier === 'high' ? 'High' : tier === 'medium' ? 'Moderate' : 'Low'}`
                        )}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={complianceFlags}
                    onCheckedChange={(v) => {
                      setComplianceFlags(!!v);
                      setPage(1);
                    }}
                  />
                  <span className="text-sm" style={{ color: colors.text.secondary }}>
                    {t('complianceFlags')}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm font-medium w-full pt-2"
                  style={{ color: colors.primary[600] }}
                >
                  {t('clearAll')}
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div
        className="overflow-x-auto rounded-lg border"
        style={{ borderColor: colors.border.light }}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tableCompany').toUpperCase()}</TableHead>
              <TableHead>{t('tableStatus').toUpperCase()}</TableHead>
              <TableHead>{t('tableRiskLevel').toUpperCase()}</TableHead>
              <TableHead>{t('tableLastUpdated').toUpperCase()}</TableHead>
              <TableHead>{t('tableRoutes').toUpperCase()}</TableHead>
              <TableHead>{t('tableActions').toUpperCase()}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedWatchlist.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8"
                  style={{ color: colors.text.secondary }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedWatchlist.map((item) => {
                const company = companyById[item.companyId];
                const name = company?.name || item.companyId;
                const initials = (name || '?').slice(0, 2).toUpperCase();
                const statusKey = getStatus(item);
                const statusColor =
                  statusKey === 'statusActive'
                    ? colors.success[500]
                    : statusKey === 'statusHighPriority'
                      ? colors.danger[500]
                      : colors.text.muted;
                const riskLevelKey = getRiskLevel(item);
                const riskWidth = getRiskLevelWidth(item);
                const routeCount = item.userCompanyInfo?.routes?.length ?? 0;
                return (
                  <TableRow key={item.companyId}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm"
                          style={{
                            backgroundColor: colors.primary[100],
                            color: colors.primary[700],
                          }}
                        >
                          {initials}
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: colors.text.primary }}>
                            {name}
                          </p>
                          <p className="text-xs" style={{ color: colors.text.secondary }}>
                            {t('companyIdFormat', { id: item.companyId })}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: statusColor }}
                        />
                        <span style={{ color: colors.text.primary }}>{t(statusKey)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div
                          className="h-1.5 rounded-full overflow-hidden w-20"
                          style={{ backgroundColor: colors.background.primary }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${riskWidth}%`,
                              backgroundColor:
                                riskLevelKey === 'riskLevelHigh'
                                  ? colors.danger[500]
                                  : riskLevelKey === 'riskLevelLow'
                                    ? colors.success[500]
                                    : colors.warning[500],
                            }}
                          />
                        </div>
                        <span className="text-xs" style={{ color: colors.text.secondary }}>
                          {t(riskLevelKey)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell style={{ color: colors.text.secondary }}>
                      {formatRelativeTime(
                        item.userCompanyInfo?.lastUpdated || item.addedDate,
                        t
                      )}
                    </TableCell>
                    <TableCell style={{ color: colors.text.secondary }}>
                      {routeCount} {t('active')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleView(item.companyId)}
                          className="font-medium"
                          style={{ color: colors.primary[500] }}
                        >
                          {t('view')}
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreVertical style={{ width: 16, height: 16 }} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleView(item.companyId)}
                            >
                              {t('view')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
        <p className="text-sm" style={{ color: colors.text.secondary }}>
          {t('showingResults', {
            from,
            to,
            total: filteredWatchlist.length,
          })}
        </p>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              />
            </PaginationItem>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(
              (p) => (
                <PaginationItem key={p}>
                  <PaginationLink
                    isActive={p === page}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </Card>
  );
}

export default WatchlistTable;
